// Frames for the scroll story (assets/kb-story-scroll.js, sections/kb-scroll-story.liquid).
// 2 Nov (council, "Apple-level finish"): the frames come from one frame pack per clip in Content → Files
// (kb-frames-b1.json …, and -m.json for phones, 720 px): every frame a small WebP of its own, so a frame decodes in a
// few milliseconds off the main thread instead of a whole 6000 × 3000 sheet stalling the page. Only the frames near the
// current one are kept decoded. If a pack is missing, the old sprite sheet for that clip (6 × 3 frames) is used instead.
window.kbStoryFrames = ({ sheets, order, canvas, pick, end, phone, changed }) => {
  const PACKS = { b1: 'kb-frames-b1', b2: 'kb-frames-b2', b3: 'kb-frames-b3', c3: 'kb-frames-c3', b4: 'kb-frames-b4' };
  const SHEET_FRAMES = 18, COLS = 6;
  const ctx = canvas.getContext('2d', { alpha: false });
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  // clips[k] = { n, size, blobs[] } from a pack, or { n: 18, sheet: Image } from the old sprite sheet
  const clips = {};
  const bitmaps = new Map(); // "k:i" -> ImageBitmap, only the frames around the current one
  const pending = new Set(), failed = new Set();
  let chain = Promise.resolve(), drawn = '';
  const requested = new Set();

  const fileUrl = (k, name) => { const u = new URL(sheets[k], location.href); u.search = ''; u.pathname = u.pathname.replace(/[^/]+$/, name); return u.href; };
  const toBlob = (b64, type) => {
    const s = atob(b64), a = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
    return new Blob([a], { type });
  };
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 120 }) : setTimeout(fn, 16));
  const size = (px) => { if (canvas.width !== px) { canvas.width = canvas.height = px; drawn = ''; } };
  const loadSheet = (k) => new Promise((done) => {
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => { clips[k] = { n: SHEET_FRAMES, sheet: im }; changed(); done(); };
    im.onerror = () => done();
    const u = sheets[k];
    im.src = phone.matches ? u + (u.includes('?') ? '&' : '?') + 'width=3600' : u;
  });
  const loadPack = (k) => {
    if (!PACKS[k] || !window.createImageBitmap) return loadSheet(k);
    return fetch(fileUrl(k, `${PACKS[k]}${phone.matches ? '-m' : ''}.json`))
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((pack) => new Promise((done) => {
        // turn the base64 frames into Blobs a few at a time, so a big pack never blocks a scroll frame
        const c = { n: pack.frames.length, size: pack.size, blobs: [] };
        let i = 0;
        const step = () => {
          const last = Math.min(c.n, i + 6);
          for (; i < last; i++) c.blobs[i] = toBlob(pack.frames[i], pack.type);
          if (i < c.n) idle(step);
          else { clips[k] = c; size(c.size); changed(); done(); }
        };
        step();
      }))
      .catch(() => loadSheet(k));
  };
  // the packs up to number n of the story order (or the clips listed, in that order), one after another, each only
  // once: b1 first, so the story can start. The story asks for each next clip a stop ahead of where it is, so a visitor
  // who never scrolls into it downloads one pack, not five
  const load = (keys = order.length - 1) => {
    for (const k of typeof keys === 'number' ? order.slice(0, keys + 1) : keys) {
      if (requested.has(k)) continue;
      requested.add(k);
      chain = chain.then(() => loadPack(k));
    }
  };

  const frameOf = (k, t) => { const c = clips[k]; return c ? Math.round(clamp(t) * (c.n - 1)) : 0; };
  const decode = (k, i) => {
    const key = `${k}:${i}`;
    if (bitmaps.has(key) || pending.has(key) || failed.has(key) || pending.size > 5) return;
    pending.add(key);
    createImageBitmap(clips[k].blobs[i]).then((bm) => {
      pending.delete(key);
      bitmaps.set(key, bm);
      changed();
    }, () => { pending.delete(key); failed.add(key); });
  };
  // keep decoded the frames a little behind and further ahead (in the direction of travel) of where the story is,
  // nearest first, and let the rest go: about 30 frames on a laptop (~120 MB), 20 on a phone (~35 MB)
  const keep = (p, dir) => {
    const ahead = phone.matches ? 0.03 : 0.035, behind = phone.matches ? 0.01 : 0.012;
    const want = new Map();
    const add = (s) => {
      for (const [k, t] of pick(clamp(s, 0, end))) {
        const c = clips[k];
        if (!c || !c.blobs) continue;
        const i = frameOf(k, t), key = `${k}:${i}`;
        if (!want.has(key)) want.set(key, [k, i]);
      }
    };
    for (let d = 0; d <= ahead + 1e-9; d += 0.001) {
      add(p + d * dir);
      if (d <= behind) add(p - d * dir);
    }
    for (const [key, bm] of bitmaps) if (!want.has(key)) { if (bm.close) bm.close(); bitmaps.delete(key); }
    for (const [k, i] of want.values()) decode(k, i);
  };

  // the source for frame i of clip k: its decoded bitmap, or the nearest decoded one while it decodes
  const source = (k, i) => {
    const c = clips[k];
    if (!c) return null;
    if (c.sheet) {
      const fw = c.sheet.naturalWidth / COLS, fh = c.sheet.naturalHeight / 3, j = Math.round((i / (c.n - 1)) * (SHEET_FRAMES - 1));
      return { im: c.sheet, sx: (j % COLS) * fw, sy: Math.floor(j / COLS) * fh, sw: fw, sh: fh, exact: true };
    }
    for (let d = 0; d < c.n; d++) {
      for (const j of d ? [i - d, i + d] : [i]) {
        const bm = bitmaps.get(`${k}:${j}`);
        if (bm) return { im: bm, sx: 0, sy: 0, sw: bm.width, sh: bm.height, exact: d === 0 };
      }
    }
    return null;
  };
  // layers: [clip, point in clip 0..1, opacity]; draws only when the picture actually changes
  const draw = (layers) => {
    const srcs = layers.map(([k, t, a]) => [source(k, frameOf(k, t)), a, `${k}:${frameOf(k, t)}:${a.toFixed(2)}`]);
    if (srcs.some(([s]) => !s)) return false;
    const key = srcs.map(([s, , id]) => (s.exact ? id : `~${id}`)).join('|');
    if (key === drawn) return true;
    drawn = key;
    srcs.forEach(([s, a], n) => {
      ctx.globalAlpha = n ? a : 1;
      ctx.drawImage(s.im, s.sx, s.sy, s.sw, s.sh, 0, 0, canvas.width, canvas.height);
    });
    ctx.globalAlpha = 1;
    return true;
  };

  return {
    load,
    keep,
    draw,
    // the canvas holds frames at their own size; CSS scales it to the stage
    fit: () => { const packed = Object.values(clips).find((c) => c.size); size(packed ? packed.size : phone.matches ? 600 : 1000); },
    shown: () => drawn !== '',
    // still settling: a frame it wants is decoding, or a stand-in frame is showing
    busy: () => pending.size > 0 || drawn.includes('~'),
  };
};
